/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSave';
import { analyzeActionSave } from './ActionSaveAnalyze';

const ActionSaveManager: ActionManagerFn<'ActionSave'> = (
  platform: Platform,
): ActionManager<'ActionSave'> => {
  return {
    ...baseActionManager(platform, 'ActionSave'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['CloudDataStore', 'DataStore'],
      },
      silent: {
        label: 'Silent',
        type: 'boolean',
        helpText:
          "Check this to prevent the message 'No changes to save!' when the user perform this action without making any changes.",
      },
    },
    name: 'Save Data Store',
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    getDisplayText: (item) => item.props.alias,
    icon: { iconName: 'save' },
    replaceableWith: ['ActionRefresh', 'ActionClear', 'ActionReset'],
    analyze: analyzeActionSave,
    description: `**Overview**<br/>
The Save Data Store action is a critical persistence component designed to commit local data changes to the backend database. It acts as the bridge between the client-side application state and the permanent storage record. When triggered, it gathers all pending modifications within the specified Data Store—including new records (Inserts), modified fields (Updates), and removed items (Deletes)—and transmits them to the server in a transactional operation.<br/><br/>
This component handles the synchronization logic automatically. If the store is "dirty" (has changes), it attempts the save; if the save is successful, the store's state is reset to "clean," and the UI reflects the persisted data. It also includes user experience features to handle scenarios where a user clicks "Save" without actually changing anything.

**Key Features**<br/>
* **Transactional Commit:** Batches all changes made to the target store and sends them to the server, ensuring that complex edits (like adding three items and deleting one) are processed together.
* **Store Compatibility:** Specifically restricted to CloudDataStore and DataStore types (allowedTypes), ensuring it only targets stores capable of server-side persistence (unlike transient or read-only stores).
* **Silent Mode:** Features a silent property to improve user experience. By default, clicking Save with no changes usually triggers a warning; this mode suppresses that warning, making it ideal for auto-save logic.
* **Component Interchangeability:** Explicitly configured to be easily swappable with ActionRefresh, ActionClear, or ActionReset, allowing developers to quickly adjust the behavior of form buttons.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property.
  * *Function:* Identifies the specific Data Store to be saved.
  * *Constraint:* Must be a CloudDataStore or generic DataStore.
* **Silent:** A boolean property.
  * *Function:* If enabled, prevents the "No changes to save!" toast message from appearing when the action is triggered on a pristine (unchanged) store.

**Use Cases**<br/>
* **Form Submission:** The primary action attached to the "Save" or "Submit" button at the bottom of a user profile or order form.
* **Auto-Save:** Triggering this action on a timer or when a user tabs out of a field, using the silent property to ensure the user isn't pestered with alerts if they haven't made new edits yet.
* **Batch Processing:** Saving a "Grid" where the user has edited multiple rows inline before deciding to commit the entire batch at once.`,
  };
};

export default ActionSaveManager;
